import { consoleApiSlice } from "./consoleApiSlice";
import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";

// Get the saved website ID from localStorage or default to null
const getSavedWebsiteId = () => {
  try {
    const savedId = localStorage.getItem("selectedConsoleWebsiteId");
    return savedId ? savedId : null;
  } catch (error) {
    console.error("Error reading from localStorage:", error);
    return null;
  }
};

// Create a slice for console website state management
const initialState = {
  selectedConsoleWebsiteId: getSavedWebsiteId(),
};

export const consoleWebsitesSlice = createSlice({
  name: "consoleWebsites",
  initialState,
  reducers: {
    setSelectedConsoleWebsite: (state, action) => {
      state.selectedConsoleWebsiteId = action.payload;
      // Save to localStorage
      try {
        localStorage.setItem("selectedConsoleWebsiteId", action.payload);
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    },
  },
});

export const { setSelectedConsoleWebsite } = consoleWebsitesSlice.actions;
export const consoleWebsitesReducer = consoleWebsitesSlice.reducer;

// Inject endpoints into the base apiSlice
export const consoleWebsitesApiSlice = consoleApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Console backend website endpoints
    // Legacy endpoint: returns all websites without pagination
    getAllConsoleWebsites: builder.query({
      query: () => "/website/",  // Trailing slash to avoid redirect
      async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const currentSelectedId =
            getState().consoleWebsites.selectedConsoleWebsiteId;

          // If we have websites but no selected ID (or the selected ID isn't in the list anymore)
          if (
            data.length > 0 &&
            (!currentSelectedId ||
              !data.some((w) => w.console_website_id === currentSelectedId))
          ) {
            dispatch(setSelectedConsoleWebsite(data[0].console_website_id));
          }
        } catch (err) {
          console.error(err);
        }
      },
      transformResponse: (responseData) => {
        return Array.isArray(responseData) ? responseData : [];
      },
      providesTags: ["Websites"],
    }),

    // Paginated endpoint: returns websites with pagination metadata
    getConsoleWebsitesPaginated: builder.query({
      query: (params = {}) => ({
        url: "/website/",  // Trailing slash to avoid redirect
        params: {
          paginate: true,
          skip: params.skip || 0,
          limit: params.limit || 50,
          search: params.search,
          archived_filter: params.archivedFilter || "not_archived",
          sort_by: params.sortBy || "date_added",
          sort_order: params.sortOrder || "desc",
        },
      }),
      transformResponse: (responseData) => {
        // Handle both paginated and legacy response formats
        if (responseData?.items) {
          return responseData;
        }
        // Fallback for legacy format
        return {
          items: Array.isArray(responseData) ? responseData : [],
          total: Array.isArray(responseData) ? responseData.length : 0,
          skip: 0,
          limit: 50,
          has_more: false,
        };
      },
      providesTags: ["Websites"],
    }),
    getConsoleWebsiteById: builder.query({
      query: (websiteId) => `/website/${websiteId}`,
      providesTags: (result, error, websiteId) => [
        { type: "Websites", id: websiteId },
      ],
    }),
    getScansByConsoleWebsiteId: builder.query({
      query: (websiteId) => `/website/${websiteId}/scans`,
      transformResponse: (responseData) =>
        Array.isArray(responseData) ? responseData : [],
      providesTags: (result, error, websiteId) => [
        { type: "WebsiteScans", id: websiteId },
        "Scans",
      ],
    }),
    getArticlesByConsoleWebsiteId: builder.query({
      query: (websiteId) => `/website/${websiteId}/articles`,
      providesTags: (result, error, websiteId) => [
        { type: "WebsiteArticles", id: websiteId },
        "Articles",
      ],
    }),
    createConsoleWebsite: builder.mutation({
      query: (website) => ({
        url: "/website/",  // Trailing slash to avoid redirect
        method: "POST",
        body: website,
      }),
      invalidatesTags: ["Websites"],
    }),
    updateConsoleWebsite: builder.mutation({
      query: ({ websiteId, website }) => ({
        url: `/website/${websiteId}`,
        method: "PUT",
        body: website,
      }),
      invalidatesTags: (result, error, { websiteId }) => [
        { type: "Websites", id: websiteId },
        "Websites",
      ],
    }),
    deleteConsoleWebsite: builder.mutation({
      query: (websiteId) => ({
        url: `/website/${websiteId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Websites"],
    }),
    archiveConsoleWebsite: builder.mutation({
      query: (websiteId) => ({
        url: `/website/${websiteId}/archive`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, websiteId) => [
        { type: "Websites", id: websiteId },
        "Websites",
      ],
    }),
    unarchiveConsoleWebsite: builder.mutation({
      query: (websiteId) => ({
        url: `/website/${websiteId}/unarchive`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, websiteId) => [
        { type: "Websites", id: websiteId },
        "Websites",
      ],
    }),
  }),
});

// Selector to get current selected console website ID
export const selectCurrentConsoleWebsiteId = (state) =>
  state.consoleWebsites.selectedConsoleWebsiteId;

// Selector to get console website by ID
export const selectConsoleWebsiteById = createSelector(
  [(state) => state.api.queries, (state, websiteId) => websiteId],
  (queries, websiteId) => {
    const websitesQuery = Object.values(queries).find(
      (query) =>
        query?.data &&
        Array.isArray(query.data) &&
        query.data.some((website) => website.console_website_id === websiteId)
    );

    return (
      websitesQuery?.data?.find(
        (website) => website.console_website_id === websiteId
      ) || null
    );
  }
);

export const {
  // Console website endpoints
  useGetAllConsoleWebsitesQuery,
  useGetConsoleWebsitesPaginatedQuery,
  useGetConsoleWebsiteByIdQuery,
  useGetScansByConsoleWebsiteIdQuery,
  useGetArticlesByConsoleWebsiteIdQuery,
  useCreateConsoleWebsiteMutation,
  useUpdateConsoleWebsiteMutation,
  useDeleteConsoleWebsiteMutation,
  useArchiveConsoleWebsiteMutation,
  useUnarchiveConsoleWebsiteMutation,
} = consoleWebsitesApiSlice;
